import { useEffect, useState } from 'react';
import { Box, IconButton, Modal, Svg } from '@hopskipdrive/react-components';
import { Button } from '@rideiq/shared/components/Button';
import { Text } from '@rideiq/shared/components/Text';
import { colorTokens } from '@rideiq/shared/components/variables';

import { formatDate, formattedIsoDate } from '../../../utils/helpers/formatDate';
import { useRiderMutations } from '../../hooks/useRiderMutations';
import { useRiderQuery } from '../../hooks/useRiderQuery';

import { Bold, DatesBox, Emphasis, TripsContainer } from './DeactivateRider.styles';
import { DeactivateRiderForm } from './DeactivateRiderForm';

type IDeactivateRider = {
  rider: RiderDetails;
  rideSchedule?: RideSchedule;
  deactivateRider: (data: { reasonKey?: string }) => Promise<void>;
  featureFlag?: boolean;
  fetchRideSchedule: (slug: string) => void;
  isLoading: boolean;
};

export const DeactivateRider = ({ riderId }: { riderId: string }) => {
  const { riderDetails, rideSchedule, fetchRiderBySlug, fetchRideSchedule, isFetchingRider } = useRiderQuery();
  const { deactivateRider } = useRiderMutations(riderId);

  useEffect(() => {
    riderId && fetchRiderBySlug(riderId);
  }, [riderId, fetchRiderBySlug]);

  if (!riderDetails) {
    return null;
  }

  return (
    <Presentation
      rider={riderDetails}
      rideSchedule={rideSchedule}
      deactivateRider={deactivateRider}
      fetchRideSchedule={fetchRideSchedule}
      isLoading={isFetchingRider}
    />
  );
};

export const Presentation = ({
  rider,
  rideSchedule,
  deactivateRider,
  fetchRideSchedule,
  isLoading,
}: IDeactivateRider) => {
  const [modalOpen, setModalOpen] = useState(false);

  const handleDeactivateRider = async (data) => {
    await deactivateRider(data);
    setModalOpen(false);
  };

  const handleOpenModal = async () => {
    if (!rideSchedule) {
      await fetchRideSchedule(rider.slug);
    }
    setModalOpen(true);
  };

  const DeactivateModal = () => {
    return (
      <Modal open={modalOpen} maxWidth="md" data-testid="deactivate-rider-modal">
        <Modal.Title sx={{ padding: '20px 24px 6px' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Text variant="h1" fontWeight={700}>
              Deactivate Rider
            </Text>
            <IconButton
              aria-label="Close deactivate rider modal"
              onClick={() => setModalOpen(false)}
              sx={{ padding: 0 }}
            >
              <Svg height="20px" icon="close-outlined" />
            </IconButton>
          </Box>
        </Modal.Title>
        <Modal.Content sx={{ padding: '0px 24px 30px' }}>
          <Text variant="subtitle2" textColor={colorTokens.text.medium}>
            {`Rider ${rider.firstName} ${rider.lastName} will no longer appear in the list of active riders and cannot be
            added to other rides.`}
          </Text>

          {!!rideSchedule?.tripCount && (
            <RidesToBeCanceled
              repeatingTrips={rideSchedule.tripTemplates}
              nonRepeatingTrips={rideSchedule.nonRepeatingTrips}
            />
          )}
          <DeactivateRiderForm handleDeactivateRider={handleDeactivateRider} />
        </Modal.Content>
        <Modal.Actions sx={{ padding: '20px 24px 16px', gap: '10px' }}>
          <Button color="tertiary" onClick={() => setModalOpen(false)}>
            Never mind
          </Button>
          <Button form="deactivate-rider-form" color="primary" type="submit">
            Confirm
          </Button>
        </Modal.Actions>
      </Modal>
    );
  };

  return rider.canDeactivate ? (
    <>
      <Button color="destructive" size="large" onClick={handleOpenModal} loading={isLoading}>
        Deactivate Rider
      </Button>
      <DeactivateModal />
    </>
  ) : null;
};

const RidesToBeCanceled = ({ repeatingTrips, nonRepeatingTrips }) => {
  const repeatingTripsUI = repeatingTrips.map((trip) => {
    return (
      trip.startTime && (
        <Box key={trip.seriesDetailText} data-testid="trip-info" display="flex" justifyContent="space-between">
          <Bold>
            {trip.startTime} {trip.anchorType}
          </Bold>
          <DatesBox>
            {trip.seriesDays.join(', ')}
            <Emphasis>{trip.seriesDetailText}</Emphasis>
          </DatesBox>
        </Box>
      )
    );
  });
  const nonRepeatingTripsUI = nonRepeatingTrips.map((trip) => (
    <Box key={trip.slug} data-testid="trip-info" display="flex" justifyContent="space-between">
      <Bold>
        {trip.startTime} {trip.anchorType.replace('_', '')}
      </Bold>
      <DatesBox>{formatDate(new Date(formattedIsoDate(trip.date)), 'MM/dd/yyyy')}</DatesBox>
    </Box>
  ));

  return (
    <Box display="flex" flexDirection="column" gap="12px">
      <Box mt="16px" mb="14px">
        <Text variant="subtitle2" textColor={colorTokens.text.medium}>
          The following scheduled rides will be canceled:
        </Text>
      </Box>
      <TripsContainer>
        {nonRepeatingTripsUI}
        {repeatingTripsUI}
      </TripsContainer>
    </Box>
  );
};
